import React, { useState } from 'react';
import I18n from 'i18n-js';
import { RCButton, RCIcon, RCToast } from '@reverbdotcom/cadence/components';
import { NudgeExploreIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing, NextBestAction } from '@reverbdotcom/commons/src/gql/graphql';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';

import QuickPriceEditModalV2 from '../listings/quick_price_edit/QuickPriceEditModalV2';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import {
  INSIGHT,
  trackFollowed,
  trackClicked,
  useTrackingContext,
  useTrackImpression,
} from './sellerRecommendationTracking';

interface Props {
  listing: Listing;
  nextBestAction: NextBestAction;
}

export const COMPONENT_NAME = 'NBAPriceDropPresentment';

export default function NBAPriceDropPresentment({ listing, nextBestAction }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const trackingContext = useTrackingContext();

  const priceMiddle = listing.priceRecommendation?.priceMiddle;

  const details = {
    recommendedPriceCents: priceMiddle?.amountCents ?? undefined,
  };

  useTrackImpression(COMPONENT_NAME, INSIGHT.PRICED_ABOVE_AVERAGE, listing.id, details);

  const {
    useLazySellerListingsQuery,
  } = React.useContext(SellerListingsCollectionContext);
  const { refetchSellerListings } = useLazySellerListingsQuery();

  return (
    <>
      <div className="seller-listing-insight-presentment">
        <span className="seller-listing-insight-presentment__price-drop-recommendation">
          <RCIcon icon={<NudgeExploreIcon />} size="inline-prominent" spacing="right" color="accent-loud" />
          <RCTextUtility size="350">
            <SanitizedRender html={nextBestAction.title ?? ''} />
          </RCTextUtility>
        </span>
        <RCTextUtility size="350">
          <SanitizedRender html={nextBestAction.subtitle ?? ''} />
        </RCTextUtility>
        <RCButton
          variant="outlined"
          size="small"
          onClick={() => {
            trackClicked(COMPONENT_NAME, INSIGHT.PRICED_ABOVE_AVERAGE, listing.id, trackingContext, details);
            setIsModalOpen(true);
          }}
          fullWidth
        >
          {nextBestAction.ctaText}
        </RCButton>
      </div>

      <QuickPriceEditModalV2
        isOpen={isModalOpen}
        listingId={listing.id}
        requestClose={() => setIsModalOpen(false)}
        onSuccessCallback={(appliedPriceCents) => {
          trackFollowed(COMPONENT_NAME, INSIGHT.PRICED_ABOVE_AVERAGE, listing.id, trackingContext, {
            ...details,
            appliedPriceCents,
          });
          setIsModalOpen(false);
          setShowToast(true);
          refetchSellerListings([listing.id]);
        }}
      />

      <RCToast
        isOpen={showToast}
        onClose={() => setShowToast(false)}
        text={I18n.t('discovery.listingsManagement.toast.message.success')}
        theme="success"
        delayInterval="short"
      />
    </>
  );
}
